import React, { createContext, useContext, useMemo } from "react";

const AUTH_USER_KEY = "adminUser";

// Memory cache for user permissions to avoid continuous disk reads
let cachedUser = null;

export const getStoredUser = () => {
  if (cachedUser) return cachedUser;
  try {
    cachedUser = JSON.parse(localStorage.getItem(AUTH_USER_KEY) || "null");
    return cachedUser;
  } catch {
    return null;
  }
};

export const setStoredUser = (user) => {
  if (!user) return;
  cachedUser = user;
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
};

export const clearStoredUser = () => {
  cachedUser = null;
  localStorage.removeItem(AUTH_USER_KEY);
};

export const isSuperAdmin = (user = getStoredUser()) => {
  const roles = user?.assignedRoles || [];
  return (
    user?.role === "super_admin" ||
    roles.some((role) => role.name === "super_admin")
  );
};

export const toSnakeCase = (value) =>
  String(value || "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

export const toPermissionKey = (value) => toSnakeCase(value).toUpperCase();

export const buildCandidates = (value) => {
  const normalized = toSnakeCase(value);
  const candidates = new Set([normalized]);

  if (normalized.endsWith("s")) {
    candidates.add(normalized.slice(0, -1));
  } else {
    candidates.add(`${normalized}s`);
  }

  return candidates;
};

// System Permissions Configuration Modules (Backend Raw Format)
export const PermissionModules = [
  { module: "Dashboard", permissions: ["Read"] },
  { module: "Users", permissions: ["Read", "Toggle", "View"] },
  {
    module: "Role",
    permissions: ["Read", "Create", "Update", "Delete", "Toggle"],
  },
  { module: "Permission", permissions: ["Read", "Toggle"] },
  {
    module: "Bank Account Details",
    permissions: ["Read", "Create", "Update", "Delete"],
  },
  {
    module: "Pickup Points",
    permissions: ["Read", "Create", "Update", "Delete"],
  },
  {
    module: "Generate Social Links",
    permissions: ["Read", "Create", "Delete"],
  },
  {
    module: "Home Screen House Design",
    permissions: ["Read", "Create", "Delete"],
  },
  {
    module: "Home Screen Consturction Our Projects",
    permissions: ["Read", "Create", "Delete"],
  },
  {
    module: "Home Screen Consturction Blogs",
    permissions: ["Read", "Create", "Delete"],
  },
  {
    module: "Home Screen Interior Design",
    permissions: ["Read", "Create", "Delete"],
  },
  {
    module: "Home Screen Consturction Shop",
    permissions: ["Read", "Create", "Delete"],
  },
  {
    module: "Home Screen Consturction Calculaotr",
    permissions: ["Read", "Create", "Delete"],
  },
  {
    module: "Out Station Zones",
    permissions: ["Read", "Create", "Update", "Delete"],
  },
  {
    module: "Out Station Base Rate",
    permissions: ["Read", "Create", "Update", "Delete"],
  },
  { module: "Cities", permissions: ["Read", "Create", "Update", "Delete"] },
  {
    module: "In Station Zones",
    permissions: ["Read", "Create", "Update", "Delete"],
  },
  {
    module: "In Station Base Rate",
    permissions: ["Read", "Create", "Update", "Delete"],
  },
  {
    module: "Shop Categories",
    permissions: ["Read", "Create", "Delete", "Update", "Toggle", "Position"],
  },
  {
    module: "Products",
    permissions: [
      "Read",
      "Create",
      "Delete",
      "Toggle",
      // "Add Discount Detail",
      "Add Related/Similar Product",
      "Update Position",
      "Position",
    ],
  },
  { module: "Quotation", permissions: ["Read", "Generate"] },
  {
    module: "Orders",
    permissions: [
      "Read",
      "View",
      "Print",
      "Download Slip",
      "View Verfiy Payments",
      "View Approved Payments",
      "View Rejected Payments",
      "View Pending Payments",
      "Accept",
      "Reject",
    ],
  },
  {
    module: "Reviews",
    permissions: [
      "Read",
      "View Pending",
      "View Approved",
      "View Rejected",
      "Approve",
      "Reject",
    ],
  },
  {
    module: "Product Delivery Charges",
    permissions: ["Read", "Create", "Update", "Delete"],
  },
  {
    module: "Brands Management",
    permissions: ["Read", "Create", "Update", "Delete"],
  },
  { module: "Banners", permissions: ["Read", "Create", "Update", "Delete"] },
  {
    module: "House Design",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "Add Similar Design",
      "Delete Similar Design",
      "Add Slide Show",
      "View",
      "Position",
      "Update Primary Image",
    ],
  },
  {
    module: "Commercial Architecture Design",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "Add Similar Design",
      "Delete Similar Design",
      "Add Slide Show",
      "View",
      "Position",
      "Update Primary Image",
    ],
  },
  {
    module: "Corporate Architecture Design",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "Add Similar Design",
      "Delete Similar Design",
      "Add Slide Show",
      "View",
      "Position",
      "Update Primary Image",
    ],
  },
  {
    module: "Front Elevations",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "Add Popular Design",
      "View",
      "Position",
      "Add Similar",
      "Delete Similar",
      "Add Popular",
    ],
  },
  {
    module: "Standard Plan Pricing",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "View",
      "Associate House Plan",
      "Update Associate House Plan",
      "Delete Aassociate House_plan",
    ],
  },
  {
    module: "Purchased house designs",
    permissions: [
      "Read All purchases",
      "Read Pending verification",
      "Read Approved",
      "Read Rejected",
      "View Detail",
    ],
  },
  {
    module: "Premium Pricing",
    permissions: ["Read", "Create", "Update", "Delete"],
  },
  {
    module: "Budget Pricing",
    permissions: ["Read", "Create", "Update", "Delete"],
  },
  {
    module: "Construction Calculators",
    permissions: ["Read", "Update", "Similar"],
  },
  { module: "Services", permissions: ["Read", "Create", "Update", "Delete"] },
  {
    module: "Service Categories",
    permissions: ["Read", "Create", "Update", "Delete"],
  },
  {
    module: "Our Project Categories",
    permissions: ["Read", "Create", "Update", "Delete", "Position", "View"],
  },
  {
    module: "Our Project Commercial Design Categories",
    permissions: ["Read", "Create", "Update", "Delete", "Position", "View"],
  },
  {
    module: "Manage Commercial Design Project",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "Toggle",
      "View",
      "Similar",
      "Position",
      "Add Similar",
      "Delete Similar",
    ],
  },
  {
    module: "Our Project Corporate Design Categories",
    permissions: ["Read", "Create", "Update", "Delete", "Position", "View"],
  },
  {
    module: "Manage Corporate Design Project",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "Toggle",
      "View",
      "Similar",
      "Position",
      "Add Similar",
      "Delete Similar",
    ],
  },
  {
    module: "Manage Project",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "Toggle",
      "View",
      "Similar",
      "Position",
      "Add Similar",
      "Delete Similar",
      // "Home Page Priority",
    ],
  },
  {
    module: "Blog Categories",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "Toggle",
      "View",
      "Position",
    ],
  },
  {
    module: "Blogs",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "Add to Most Popular",
      "View",
      "Add Featured Blog",
      "Position",
      // "Home Page Priority",
    ],
  },
  {
    module: "Interior Design Categories",
    permissions: ["Read", "Create", "Update", "Delete", "Toggle", "Position"],
  },
  {
    module: "Interior Design",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "Add Popular Design",
      "View",
      "Position",
      "Home Screen Priority",
      "Similar Position",
      "Add Similar",
      "Delete Similar",
    ],
  },
  {
    module: "Commercial Interior Design Categories",
    permissions: ["Read", "Create", "Update", "Delete", "Toggle", "Position"],
  },
  {
    module: "Commercial Interior Design",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "Add Popular Design",
      "View",
      "Position",
      "Home Screen Priority",
      "Similar Position",
      "Add Similar",
      "Delete Similar",
    ],
  },
  {
    module: "Corporate Interior Design Categories",
    permissions: ["Read", "Create", "Update", "Delete", "Toggle", "Position"],
  },
  {
    module: "Corporate Interior Design",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "Add Popular Design",
      "View",
      "Position",
      "Home Screen Priority",
      "Similar Position",
      "Add Similar",
      "Delete Similar",
    ],
  },
  {
    module: "Contract Categories",
    permissions: ["Read", "Create", "Update", "Delete", "Position"],
  },
  {
    module: "Contract",
    permissions: [
      "Read",
      "Create",
      "Update",
      "Delete",
      "View/Make",
      "View User Pdf",
      "Download User Pdf",
    ],
  },
  {
    module: "Design Contract",
    permissions: ["Read", "Create", "Update", "Delete", "View/Make"],
  },
  {
    module: "Similar Items",
    permissions: ["Read", "Create", "Delete", "Position"],
  },
  {
    module: "SEO Management",
    permissions: ["Read", "Create", "Update", "Delete"],
  },
  {
    module: "Static Pages",
    permissions: ["Read", "Create", "Update"],
  },
  {
    module: "Translations",
    permissions: ["Read", "Create", "Update"],
  },
  {
    module: "Tentative Cost",
    permissions: ["Create", "Update", "Read", "Delete"],
  },
];

export const SystemPermissions = Object.freeze({
  Dashboard: Object.freeze({
    READ: { action: "read", resource: "dashboard" },
  }),
  Users: Object.freeze({
    READ: { action: "read", resource: "users" },
    TOGGLE: { action: "toggle", resource: "users" },
    VIEW: { action: "view", resource: "users" },
  }),
  Role: Object.freeze({
    READ: { action: "read", resource: "role" },
    CREATE: { action: "create", resource: "role" },
    UPDATE: { action: "update", resource: "role" },
    DELETE: { action: "delete", resource: "role" },
    TOGGLE: { action: "toggle", resource: "role" },
  }),
  Permission: Object.freeze({
    READ: { action: "read", resource: "permission" },
    TOGGLE: { action: "toggle", resource: "permission" },
  }),
  BankAccountDetails: Object.freeze({
    READ: { action: "read", resource: "bank_account_details" },
    CREATE: { action: "create", resource: "bank_account_details" },
    UPDATE: { action: "update", resource: "bank_account_details" },
    DELETE: { action: "delete", resource: "bank_account_details" },
  }),
  PickupPoints: Object.freeze({
    READ: { action: "read", resource: "pickup_points" },
    CREATE: { action: "create", resource: "pickup_points" },
    UPDATE: { action: "update", resource: "pickup_points" },
    DELETE: { action: "delete", resource: "pickup_points" },
  }),
  GenerateSocialLinks: Object.freeze({
    READ: { action: "read", resource: "generate_social_links" },
    CREATE: { action: "create", resource: "generate_social_links" },
    DELETE: { action: "delete", resource: "generate_social_links" },
  }),
  HomeScreenHouseDesign: Object.freeze({
    READ: { action: "read", resource: "home_screen_house_design" },
    CREATE: { action: "create", resource: "home_screen_house_design" },
    DELETE: { action: "delete", resource: "home_screen_house_design" },
  }),
  HomeScreenConsturctionOurProjects: Object.freeze({
    READ: { action: "read", resource: "home_screen_consturction_our_projects" },
    CREATE: {
      action: "create",
      resource: "home_screen_consturction_our_projects",
    },
    DELETE: {
      action: "delete",
      resource: "home_screen_consturction_our_projects",
    },
  }),
  HomeScreenConsturctionBlogs: Object.freeze({
    READ: { action: "read", resource: "home_screen_consturction_blogs" },
    CREATE: { action: "create", resource: "home_screen_consturction_blogs" },
    DELETE: { action: "delete", resource: "home_screen_consturction_blogs" },
  }),
  HomeScreenInteriorDesign: Object.freeze({
    READ: { action: "read", resource: "home_screen_interior_design" },
    CREATE: { action: "create", resource: "home_screen_interior_design" },
    DELETE: { action: "delete", resource: "home_screen_interior_design" },
  }),
  HomeScreenConsturctionShop: Object.freeze({
    READ: { action: "read", resource: "home_screen_consturction_shop" },
    CREATE: { action: "create", resource: "home_screen_consturction_shop" },
    DELETE: { action: "delete", resource: "home_screen_consturction_shop" },
  }),
  HomeScreenConsturctionCalculaotr: Object.freeze({
    READ: { action: "read", resource: "home_screen_consturction_calculaotr" },
    CREATE: {
      action: "create",
      resource: "home_screen_consturction_calculaotr",
    },
    DELETE: {
      action: "delete",
      resource: "home_screen_consturction_calculaotr",
    },
  }),
  OutStationZones: Object.freeze({
    READ: { action: "read", resource: "out_station_zones" },
    CREATE: { action: "create", resource: "out_station_zones" },
    UPDATE: { action: "update", resource: "out_station_zones" },
    DELETE: { action: "delete", resource: "out_station_zones" },
  }),
  OutStationBaseRate: Object.freeze({
    READ: { action: "read", resource: "out_station_base_rate" },
    CREATE: { action: "create", resource: "out_station_base_rate" },
    UPDATE: { action: "update", resource: "out_station_base_rate" },
    DELETE: { action: "delete", resource: "out_station_base_rate" },
  }),
  Cities: Object.freeze({
    READ: { action: "read", resource: "cities" },
    CREATE: { action: "create", resource: "cities" },
    UPDATE: { action: "update", resource: "cities" },
    DELETE: { action: "delete", resource: "cities" },
  }),
  InStationZones: Object.freeze({
    READ: { action: "read", resource: "in_station_zones" },
    CREATE: { action: "create", resource: "in_station_zones" },
    UPDATE: { action: "update", resource: "in_station_zones" },
    DELETE: { action: "delete", resource: "in_station_zones" },
  }),
  InStationBaseRate: Object.freeze({
    READ: { action: "read", resource: "in_station_base_rate" },
    CREATE: { action: "create", resource: "in_station_base_rate" },
    UPDATE: { action: "update", resource: "in_station_base_rate" },
    DELETE: { action: "delete", resource: "in_station_base_rate" },
  }),
  ShopCategories: Object.freeze({
    READ: { action: "read", resource: "shop_categories" },
    CREATE: { action: "create", resource: "shop_categories" },
    DELETE: { action: "delete", resource: "shop_categories" },
    TOGGLE: { action: "toggle", resource: "shop_categories" },
    UPDATE: { action: "update", resource: "shop_categories" },
    POSITION: { action: "position", resource: "shop_categories" },
  }),
  Products: Object.freeze({
    READ: { action: "read", resource: "products" },
    CREATE: { action: "create", resource: "products" },
    DELETE: { action: "delete", resource: "products" },
    TOGGLE: { action: "toggle", resource: "products" },
    ADD_DISCOUNT_DETAIL: {
      action: "add_discount_detail",
      resource: "products",
    },
    ADD_RELATED_SIMILAR_PRODUCT: {
      action: "add_related_similar_product",
      resource: "products",
    },
    UPDATE_POSITION: { action: "update_position", resource: "products" },
    POSITION: { action: "position", resource: "products" },
  }),
  Quotation: Object.freeze({
    READ: { action: "read", resource: "quotation" },
    GENERATE: { action: "generate", resource: "quotation" },
  }),
  Orders: Object.freeze({
    READ: { action: "read", resource: "orders" },
    VIEW: { action: "view", resource: "orders" },
    PRINT: { action: "print", resource: "orders" },
    DOWNLOAD_SLIP: { action: "download_slip", resource: "orders" },
    VIEW_VERFIY_PAYMENTS: {
      action: "view_verfiy_payments",
      resource: "orders",
    },
    VIEW_APPROVED_PAYMENTS: {
      action: "view_approved_payments",
      resource: "orders",
    },
    VIEW_REJECTED_PAYMENTS: {
      action: "view_rejected_payments",
      resource: "orders",
    },
    VIEW_PENDING_PAYMENTS: {
      action: "view_pending_payments",
      resource: "orders",
    },
    ACCEPT: { action: "accept", resource: "orders" },
    REJECT: { action: "reject", resource: "orders" },
  }),
  Reviews: Object.freeze({
    READ: { action: "read", resource: "reviews" },
    VIEW_PENDING: { action: "view_pending", resource: "reviews" },
    VIEW_APPROVED: { action: "view_approved", resource: "reviews" },
    VIEW_REJECTED: { action: "view_rejected", resource: "reviews" },
    APPROVE: { action: "approve", resource: "reviews" },
    REJECT: { action: "reject", resource: "reviews" },
  }),
  ProductDeliveryCharges: Object.freeze({
    READ: { action: "read", resource: "product_delivery_charges" },
    CREATE: { action: "create", resource: "product_delivery_charges" },
    UPDATE: { action: "update", resource: "product_delivery_charges" },
    DELETE: { action: "delete", resource: "product_delivery_charges" },
  }),
  BrandsManagement: Object.freeze({
    READ: { action: "read", resource: "brands_management" },
    CREATE: { action: "create", resource: "brands_management" },
    UPDATE: { action: "update", resource: "brands_management" },
    DELETE: { action: "delete", resource: "brands_management" },
  }),
  Banners: Object.freeze({
    READ: { action: "read", resource: "banners" },
    CREATE: { action: "create", resource: "banners" },
    UPDATE: { action: "update", resource: "banners" },
    DELETE: { action: "delete", resource: "banners" },
  }),
  HouseDesign: Object.freeze({
    READ: { action: "read", resource: "house_design" },
    CREATE: { action: "create", resource: "house_design" },
    UPDATE: { action: "update", resource: "house_design" },
    DELETE: { action: "delete", resource: "house_design" },
    ADD_SIMILAR_DESIGN: {
      action: "add_similar_design",
      resource: "house_design",
    },
    DELETE_SIMILAR_DESIGN: {
      action: "delete_similar_design",
      resource: "house_design",
    },
    ADD_SLIDE_SHOW: { action: "add_slide_show", resource: "house_design" },
    VIEW: { action: "view", resource: "house_design" },
    POSITION: { action: "position", resource: "house_design" },
    UPDATE_PRIMARY_IMAGE: {
      action: "update_primary_image",
      resource: "house_design",
    },
  }),
  CommercialArchitectureDesign: Object.freeze({
    READ: { action: "read", resource: "commercial_architecture_design" },
    CREATE: { action: "create", resource: "commercial_architecture_design" },
    UPDATE: { action: "update", resource: "commercial_architecture_design" },
    DELETE: { action: "delete", resource: "commercial_architecture_design" },
    ADD_SIMILAR_DESIGN: {
      action: "add_similar_design",
      resource: "commercial_architecture_design",
    },
    DELETE_SIMILAR_DESIGN: {
      action: "delete_similar_design",
      resource: "commercial_architecture_design",
    },
    ADD_SLIDE_SHOW: { action: "add_slide_show", resource: "commercial_architecture_design" },
    VIEW: { action: "view", resource: "commercial_architecture_design" },
    POSITION: { action: "position", resource: "commercial_architecture_design" },
    UPDATE_PRIMARY_IMAGE: {
      action: "update_primary_image",
      resource: "commercial_architecture_design",
    },
  }),
  CorporateArchitectureDesign: Object.freeze({
    READ: { action: "read", resource: "corporate_architecture_design" },
    CREATE: { action: "create", resource: "corporate_architecture_design" },
    UPDATE: { action: "update", resource: "corporate_architecture_design" },
    DELETE: { action: "delete", resource: "corporate_architecture_design" },
    ADD_SIMILAR_DESIGN: {
      action: "add_similar_design",
      resource: "corporate_architecture_design",
    },
    DELETE_SIMILAR_DESIGN: {
      action: "delete_similar_design",
      resource: "corporate_architecture_design",
    },
    ADD_SLIDE_SHOW: { action: "add_slide_show", resource: "corporate_architecture_design" },
    VIEW: { action: "view", resource: "corporate_architecture_design" },
    POSITION: { action: "position", resource: "corporate_architecture_design" },
    UPDATE_PRIMARY_IMAGE: {
      action: "update_primary_image",
      resource: "corporate_architecture_design",
    },
  }),
  FrontElevations: Object.freeze({
    READ: { action: "read", resource: "front_elevations" },
    CREATE: { action: "create", resource: "front_elevations" },
    UPDATE: { action: "update", resource: "front_elevations" },
    DELETE: { action: "delete", resource: "front_elevations" },
    ADD_POPULAR_DESIGN: {
      action: "add_popular_design",
      resource: "front_elevations",
    },
    VIEW: { action: "view", resource: "front_elevations" },
    POSITION: { action: "position", resource: "front_elevations" },
    ADD_SIMILAR: { action: "add_similar", resource: "front_elevations" },
    DELETE_SIMILAR: { action: "delete_similar", resource: "front_elevations" },
    ADD_POPULAR: { action: "add_popular", resource: "front_elevations" },
  }),
  StandardPlanPricing: Object.freeze({
    READ: { action: "read", resource: "standard_plan_pricing" },
    CREATE: { action: "create", resource: "standard_plan_pricing" },
    UPDATE: { action: "update", resource: "standard_plan_pricing" },
    DELETE: { action: "delete", resource: "standard_plan_pricing" },
    VIEW: { action: "view", resource: "standard_plan_pricing" },
    ASSOCIATE_HOUSE_PLAN: {
      action: "associate_house_plan",
      resource: "standard_plan_pricing",
    },
    UPDATE_ASSOCIATE_HOUSE_PLAN: {
      action: "update_associate_house_plan",
      resource: "standard_plan_pricing",
    },
    DELETE_AASSOCIATE_HOUSE_PLAN: {
      action: "delete_aassociate_house_plan",
      resource: "standard_plan_pricing",
    },
  }),
  PurchasedHouseDesigns: Object.freeze({
    READ_ALL_PURCHASES: {
      action: "read_all_purchases",
      resource: "purchased_house_designs",
    },
    READ_PENDING_VERIFICATION: {
      action: "read_pending_verification",
      resource: "purchased_house_designs",
    },
    READ_APPROVED: {
      action: "read_approved",
      resource: "purchased_house_designs",
    },
    READ_REJECTED: {
      action: "read_rejected",
      resource: "purchased_house_designs",
    },
    VIEW_DETAIL: { action: "view_detail", resource: "purchased_house_designs" },
  }),
  PremiumPricing: Object.freeze({
    READ: { action: "read", resource: "premium_pricing" },
    CREATE: { action: "create", resource: "premium_pricing" },
    UPDATE: { action: "update", resource: "premium_pricing" },
    DELETE: { action: "delete", resource: "premium_pricing" },
  }),
  BudgetPricing: Object.freeze({
    READ: { action: "read", resource: "budget_pricing" },
    CREATE: { action: "create", resource: "budget_pricing" },
    UPDATE: { action: "update", resource: "budget_pricing" },
    DELETE: { action: "delete", resource: "budget_pricing" },
  }),
  ConstructionCalculators: Object.freeze({
    READ: { action: "read", resource: "construction_calculators" },
    UPDATE: { action: "update", resource: "construction_calculators" },
    SIMILAR: { action: "similar", resource: "construction_calculators" },
  }),
  Services: Object.freeze({
    READ: { action: "read", resource: "services" },
    CREATE: { action: "create", resource: "services" },
    UPDATE: { action: "update", resource: "services" },
    DELETE: { action: "delete", resource: "services" },
  }),
  ServiceCategories: Object.freeze({
    READ: { action: "read", resource: "service_categories" },
    CREATE: { action: "create", resource: "service_categories" },
    UPDATE: { action: "update", resource: "service_categories" },
    DELETE: { action: "delete", resource: "service_categories" },
  }),
  OurProjectCategories: Object.freeze({
    READ: { action: "read", resource: "our_project_categories" },
    CREATE: { action: "create", resource: "our_project_categories" },
    UPDATE: { action: "update", resource: "our_project_categories" },
    DELETE: { action: "delete", resource: "our_project_categories" },
    VIEW: { action: "view", resource: "our_project_categories" },
    POSITION: { action: "position", resource: "our_project_categories" },
  }),
  OurProjectCommercialDesignCategories: Object.freeze({
    READ: { action: "read", resource: "our_project_commercial_design_categories" },
    CREATE: { action: "create", resource: "our_project_commercial_design_categories" },
    UPDATE: { action: "update", resource: "our_project_commercial_design_categories" },
    DELETE: { action: "delete", resource: "our_project_commercial_design_categories" },
    VIEW: { action: "view", resource: "our_project_commercial_design_categories" },
    POSITION: { action: "position", resource: "our_project_commercial_design_categories" },
  }),
  ManageCommercialDesignProject: Object.freeze({
    READ: { action: "read", resource: "manage_commercial_design_project" },
    CREATE: { action: "create", resource: "manage_commercial_design_project" },
    UPDATE: { action: "update", resource: "manage_commercial_design_project" },
    DELETE: { action: "delete", resource: "manage_commercial_design_project" },
    TOGGLE: { action: "toggle", resource: "manage_commercial_design_project" },
    VIEW: { action: "view", resource: "manage_commercial_design_project" },
    SIMILAR: { action: "similar", resource: "manage_commercial_design_project" },
    POSITION: { action: "position", resource: "manage_commercial_design_project" },
    ADD_SIMILAR: { action: "add_similar", resource: "manage_commercial_design_project" },
    DELETE_SIMILAR: { action: "delete_similar", resource: "manage_commercial_design_project" },
  }),
  OurProjectCorporateDesignCategories: Object.freeze({
    READ: { action: "read", resource: "our_project_corporate_design_categories" },
    CREATE: { action: "create", resource: "our_project_corporate_design_categories" },
    UPDATE: { action: "update", resource: "our_project_corporate_design_categories" },
    DELETE: { action: "delete", resource: "our_project_corporate_design_categories" },
    VIEW: { action: "view", resource: "our_project_corporate_design_categories" },
    POSITION: { action: "position", resource: "our_project_corporate_design_categories" },
  }),
  ManageCorporateDesignProject: Object.freeze({
    READ: { action: "read", resource: "manage_corporate_design_project" },
    CREATE: { action: "create", resource: "manage_corporate_design_project" },
    UPDATE: { action: "update", resource: "manage_corporate_design_project" },
    DELETE: { action: "delete", resource: "manage_corporate_design_project" },
    TOGGLE: { action: "toggle", resource: "manage_corporate_design_project" },
    VIEW: { action: "view", resource: "manage_corporate_design_project" },
    SIMILAR: { action: "similar", resource: "manage_corporate_design_project" },
    POSITION: { action: "position", resource: "manage_corporate_design_project" },
    ADD_SIMILAR: { action: "add_similar", resource: "manage_corporate_design_project" },
    DELETE_SIMILAR: { action: "delete_similar", resource: "manage_corporate_design_project" },
  }),
  ManageProject: Object.freeze({
    READ: { action: "read", resource: "manage_project" },
    CREATE: { action: "create", resource: "manage_project" },
    UPDATE: { action: "update", resource: "manage_project" },
    DELETE: { action: "delete", resource: "manage_project" },
    TOGGLE: { action: "toggle", resource: "manage_project" },
    VIEW: { action: "view", resource: "manage_project" },
    SIMILAR: { action: "similar", resource: "manage_project" },
    POSITION: { action: "position", resource: "manage_project" },
    ADD_SIMILAR: { action: "add_similar", resource: "manage_project" },
    DELETE_SIMILAR: { action: "delete_similar", resource: "manage_project" },
    HOME_PAGE_PRIORITY: {
      action: "home_page_priority",
      resource: "manage_project",
    },
  }),
  BlogCategories: Object.freeze({
    READ: { action: "read", resource: "blog_categories" },
    CREATE: { action: "create", resource: "blog_categories" },
    UPDATE: { action: "update", resource: "blog_categories" },
    DELETE: { action: "delete", resource: "blog_categories" },
    TOGGLE: { action: "toggle", resource: "blog_categories" },
    VIEW: { action: "view", resource: "blog_categories" },
    POSITION: { action: "position", resource: "blog_categories" },
  }),
  Blogs: Object.freeze({
    READ: { action: "read", resource: "blogs" },
    CREATE: { action: "create", resource: "blogs" },
    UPDATE: { action: "update", resource: "blogs" },
    DELETE: { action: "delete", resource: "blogs" },
    ADD_TO_MOST_POPULAR: { action: "add_to_most_popular", resource: "blogs" },
    VIEW: { action: "view", resource: "blogs" },
    ADD_FEATURED_BLOG: { action: "add_featured_blog", resource: "blogs" },
    POSITION: { action: "position", resource: "blogs" },
    HOME_PAGE_PRIORITY: { action: "home_page_priority", resource: "blogs" },
  }),
  InteriorDesignCategories: Object.freeze({
    READ: { action: "read", resource: "interior_design_categories" },
    CREATE: { action: "create", resource: "interior_design_categories" },
    UPDATE: { action: "update", resource: "interior_design_categories" },
    DELETE: { action: "delete", resource: "interior_design_categories" },
    TOGGLE: { action: "toggle", resource: "interior_design_categories" },
    POSITION: { action: "position", resource: "interior_design_categories" },
  }),
  InteriorDesign: Object.freeze({
    READ: { action: "read", resource: "interior_design" },
    CREATE: { action: "create", resource: "interior_design" },
    UPDATE: { action: "update", resource: "interior_design" },
    DELETE: { action: "delete", resource: "interior_design" },
    ADD_POPULAR_DESIGN: {
      action: "add_popular_design",
      resource: "interior_design",
    },
    VIEW: { action: "view", resource: "interior_design" },
    POSITION: { action: "position", resource: "interior_design" },
    HOME_SCREEN_PRIORITY: {
      action: "home_screen_priority",
      resource: "interior_design",
    },
    SIMILAR_POSITION: {
      action: "similar_position",
      resource: "interior_design",
    },
    ADD_SIMILAR: { action: "add_similar", resource: "interior_design" },
    DELETE_SIMILAR: { action: "delete_similar", resource: "interior_design" },
  }),
  CommercialInteriorDesignCategories: Object.freeze({
    READ: { action: "read", resource: "commercial_interior_design_categories" },
    CREATE: { action: "create", resource: "commercial_interior_design_categories" },
    UPDATE: { action: "update", resource: "commercial_interior_design_categories" },
    DELETE: { action: "delete", resource: "commercial_interior_design_categories" },
    TOGGLE: { action: "toggle", resource: "commercial_interior_design_categories" },
    POSITION: { action: "position", resource: "commercial_interior_design_categories" },
  }),
  CommercialInteriorDesign: Object.freeze({
    READ: { action: "read", resource: "commercial_interior_design" },
    CREATE: { action: "create", resource: "commercial_interior_design" },
    UPDATE: { action: "update", resource: "commercial_interior_design" },
    DELETE: { action: "delete", resource: "commercial_interior_design" },
    ADD_POPULAR_DESIGN: {
      action: "add_popular_design",
      resource: "commercial_interior_design",
    },
    VIEW: { action: "view", resource: "commercial_interior_design" },
    POSITION: { action: "position", resource: "commercial_interior_design" },
    HOME_SCREEN_PRIORITY: {
      action: "home_screen_priority",
      resource: "commercial_interior_design",
    },
    SIMILAR_POSITION: {
      action: "similar_position",
      resource: "commercial_interior_design",
    },
    ADD_SIMILAR: { action: "add_similar", resource: "commercial_interior_design" },
    DELETE_SIMILAR: { action: "delete_similar", resource: "commercial_interior_design" },
  }),
  CorporateInteriorDesignCategories: Object.freeze({
    READ: { action: "read", resource: "corporate_interior_design_categories" },
    CREATE: { action: "create", resource: "corporate_interior_design_categories" },
    UPDATE: { action: "update", resource: "corporate_interior_design_categories" },
    DELETE: { action: "delete", resource: "corporate_interior_design_categories" },
    TOGGLE: { action: "toggle", resource: "corporate_interior_design_categories" },
    POSITION: { action: "position", resource: "corporate_interior_design_categories" },
  }),
  CorporateInteriorDesign: Object.freeze({
    READ: { action: "read", resource: "corporate_interior_design" },
    CREATE: { action: "create", resource: "corporate_interior_design" },
    UPDATE: { action: "update", resource: "corporate_interior_design" },
    DELETE: { action: "delete", resource: "corporate_interior_design" },
    ADD_POPULAR_DESIGN: {
      action: "add_popular_design",
      resource: "corporate_interior_design",
    },
    VIEW: { action: "view", resource: "corporate_interior_design" },
    POSITION: { action: "position", resource: "corporate_interior_design" },
    HOME_SCREEN_PRIORITY: {
      action: "home_screen_priority",
      resource: "corporate_interior_design",
    },
    SIMILAR_POSITION: {
      action: "similar_position",
      resource: "corporate_interior_design",
    },
    ADD_SIMILAR: { action: "add_similar", resource: "corporate_interior_design" },
    DELETE_SIMILAR: { action: "delete_similar", resource: "corporate_interior_design" },
  }),
  ContractCategories: Object.freeze({
    READ: { action: "read", resource: "contract_categories" },
    CREATE: { action: "create", resource: "contract_categories" },
    UPDATE: { action: "update", resource: "contract_categories" },
    DELETE: { action: "delete", resource: "contract_categories" },
    POSITION: { action: "position", resource: "contract_categories" },
  }),
  Contract: Object.freeze({
    READ: { action: "read", resource: "contract" },
    CREATE: { action: "create", resource: "contract" },
    UPDATE: { action: "update", resource: "contract" },
    DELETE: { action: "delete", resource: "contract" },
    VIEW_MAKE: { action: "view_make", resource: "contract" },
    VIEW_USER_PDF: { action: "view_user_pdf", resource: "contract" },
    DOWNLOAD_USER_PDF: { action: "download_user_pdf", resource: "contract" },
  }),
  DesignContract: Object.freeze({
    READ: { action: "read", resource: "design_contract" },
    CREATE: { action: "create", resource: "design_contract" },
    UPDATE: { action: "update", resource: "design_contract" },
    DELETE: { action: "delete", resource: "design_contract" },
    VIEW_MAKE: { action: "view_make", resource: "design_contract" },
    VIEW_USER_PDF: { action: "view_user_pdf", resource: "design_contract" },
    DOWNLOAD_USER_PDF: {
      action: "download_user_pdf",
      resource: "design_contract",
    },
  }),
  SimilarItems: Object.freeze({
    READ: { action: "read", resource: "similar_items" },
    CREATE: { action: "create", resource: "similar_items" },
    DELETE: { action: "delete", resource: "similar_items" },
    POSITION: { action: "position", resource: "similar_items" },
  }),
  SeoManagement: Object.freeze({
    READ: { action: "read", resource: "seo_management" },
    CREATE: { action: "create", resource: "seo_management" },
    UPDATE: { action: "update", resource: "seo_management" },
    DELETE: { action: "delete", resource: "seo_management" },
  }),
  StaticPages: Object.freeze({
    READ: { action: "read", resource: "static_pages" },
    CREATE: { action: "create", resource: "static_pages" },
    UPDATE: { action: "update", resource: "static_pages" },
  }),
  Translations: Object.freeze({
    READ: { action: "read", resource: "translations" },
    CREATE: { action: "create", resource: "translations" },
    UPDATE: { action: "update", resource: "translations" },
  }),
  TentativeCost: Object.freeze({
    CREATE: { action: "create", resource: "tentative_cost" },
    UPDATE: { action: "update", resource: "tentative_cost" },
    READ: { action: "read", resource: "tentative_cost" },
    DELETE: { action: "delete", resource: "tentative_cost" },
  }),

  // Aliases for backward compatibility with older key names
  AddCities: Object.freeze({
    READ: { action: "read", resource: "cities" },
    CREATE: { action: "create", resource: "cities" },
    UPDATE: { action: "update", resource: "cities" },
    DELETE: { action: "delete", resource: "cities" },
  }),
  ManageBanners: Object.freeze({
    READ: { action: "read", resource: "banners" },
    CREATE: { action: "create", resource: "banners" },
    UPDATE: { action: "update", resource: "banners" },
    DELETE: { action: "delete", resource: "banners" },
  }),
  GenerateQuotation: Object.freeze({
    READ: { action: "read", resource: "quotation" },
    GENERATE: { action: "generate", resource: "quotation" },
  }),
  CalculatorsList: Object.freeze({
    READ: { action: "read", resource: "construction_calculators" },
    UPDATE: { action: "update", resource: "construction_calculators" },
    SIMILAR: { action: "similar", resource: "construction_calculators" },
  }),
});

export const flattenPermissions = (permissions) => {
  const permissionList = [];

  Object.values(permissions).forEach((permissionValue) => {
    if (permissionValue && permissionValue.action && permissionValue.resource) {
      permissionList.push(permissionValue);
      return;
    }

    if (permissionValue && typeof permissionValue === "object") {
      permissionList.push(...flattenPermissions(permissionValue));
    }
  });

  return permissionList;
};

export const permissionList = flattenPermissions(SystemPermissions);

export const hasPermission = (permission, user = getStoredUser()) => {
  if (!permission) return true;
  if (isSuperAdmin(user)) return true;

  if (Array.isArray(permission)) {
    return permission.some((perm) => hasPermission(perm, user));
  }

  const permissions = user?.permissions || [];

  let targetActions = [];
  let targetResource = "";

  if (permission && typeof permission === "object") {
    if (permission.actions) {
      targetActions = Array.isArray(permission.actions)
        ? permission.actions
        : [permission.actions];
      targetResource = permission.resource;
    } else if (permission.action) {
      targetActions = [permission.action];
      targetResource = permission.resource;
    }
  } else if (typeof permission === "string") {
    targetActions = ["read"];
    targetResource = permission;
  }

  if (!targetResource) return true;

  const resourceCandidates = buildCandidates(targetResource);
  const actionCandidates = new Set(
    targetActions.map((act) => toSnakeCase(act)),
  );

  const canReadWithAnyModuleAction = actionCandidates.has("read");

  return permissions.some((userPermission) => {
    if (userPermission.status === false) return false;

    const normalizedUserResource = toSnakeCase(userPermission.resource);
    if (!resourceCandidates.has(normalizedUserResource)) return false;

    if (canReadWithAnyModuleAction) return true;

    return actionCandidates.has(toSnakeCase(userPermission.action));
  });
};

export const filterRoutesByPermissions = (items, user = getStoredUser()) => {
  if (isSuperAdmin(user)) return items;

  return items.reduce((allowedRoutes, item) => {
    if (item.requiresSuperAdmin) {
      return allowedRoutes;
    }

    const children = item.children
      ? filterRoutesByPermissions(item.children, user)
      : undefined;
    const routeAllowed = hasPermission(item.permission, user);

    if (item.isGroup) {
      if (children?.length) {
        allowedRoutes.push({ ...item, children });
      }
      return allowedRoutes;
    }

    if (routeAllowed) {
      allowedRoutes.push(children ? { ...item, children } : item);
    }

    return allowedRoutes;
  }, []);
};

export const getFirstAllowedPath = (items) => {
  for (const item of items) {
    if (item.visible === false) continue;
    if (item.path) return item.path;

    if (item.children) {
      const childPath = getFirstAllowedPath(item.children);
      if (childPath) return childPath;
    }
  }

  return null;
};

// React context for dynamic user state and updates
const PermissionContext = createContext(null);

export const PermissionProvider = ({ user, children }) => {
  const value = useMemo(
    () => ({
      user,
      hasPermission: (permission) => hasPermission(permission, user),
    }),
    [user],
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermission must be used within a PermissionProvider");
  }
  return context;
};
